import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import moment from 'moment';
import isObject from 'lodash-es/isObject';
import { UtilityMeterState, UtilityValue } from './utility-meter-interfaces';

@Component({
  tag: 'lar-utility-meter',
  styleUrl: 'utility-meter.scss',
  shadow: true
})
export class UtilityMeter {
  @Element() el!: HTMLElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'chart-bar';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
   @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() loading: boolean = true;
  @State() values: UtilityValue[] = [];
  private node!: HTMLLarNodeElement;
  private measuringUnit: string = '';
  private webComponentValueType: 'abs'|'rel' = 'abs';

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }

  /**
   * Larva input message
   */
  @Method()
  async input(state: UtilityMeterState) {
    this.loading = false;
    if (isObject(state) && state.months) {
      this.values = state.months;
      // tslint:disable-next-line: strict-boolean-conditions
      if (state.webComponentValueType) {
        this.webComponentValueType = state.webComponentValueType;
      }
      // tslint:disable-next-line: strict-boolean-conditions
      if (state.measuringUnit) {
        this.measuringUnit = state.measuringUnit;
      }
    }
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;

    this.output.emit();
  }

  render() {
    const months = this.values && Array.isArray(this.values) ? this.values : [];
    let latestMonth = months.length > 0 ? months[0] : null;
    if (latestMonth && latestMonth.date !== moment().format('MM-YYYY')) {
      latestMonth = null;
    }

    let value: string;
    switch (this.webComponentValueType) {
      case 'abs': {
        if (latestMonth) {
          value = `${latestMonth.abs}${this.measuringUnit}`;
        }
        break;
      }
      case 'rel': {
        if (latestMonth) {
          value = `${latestMonth.rel}${this.measuringUnit}`;
        }
        break;
      }
      default:
        // none
    }

    const componentProps = {
      measuringUnit: this.measuringUnit,
      webComponentValueType: this.webComponentValueType,
      values: JSON.stringify(this.values)
    };
    return [
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={this.colorIconSmall}
        log={this.log}
        loading={this.loading}
        nodeSize={this.nodeSize}
        component="lar-utility-log"
        componentProps={componentProps}
      >
        <slot />
      </lar-node>
    ];
  }
}
